// hooks/academicPlan/helpers.ts
/**
 * Helper functions for academic plan operations
 * Extracted from useAcademicPlan for reuse across split hooks
 */

import { API_URL as API_BASE_URL } from '../../utils/constants';
import { toDomainSemesters } from '../../adapters/plan';
import type { SemestersMap, EnrolledCourse } from '../../types';

export { API_BASE_URL };

// =============================================================================
// TYPES
// =============================================================================

/**
 * Placeholder requirements structure
 */
export interface PlaceholderRequirements {
  department?: string;
  levelMin?: number | null;
  levelMax?: number | null;
  genEdCategories?: string[];
  keywords?: string[];
}

/**
 * Options for creating a placeholder course
 */
export interface CreatePlaceholderOptions {
  title?: string;
  credits?: number;
  placeholderType?: string;
  requirements?: PlaceholderRequirements;
  notes?: string;
  color?: string;
}

/**
 * Placeholder course structure
 */
export interface PlaceholderCourse extends EnrolledCourse {
  isPlaceholder: true;
  placeholderType: string;
  requirements: PlaceholderRequirements;
  notes: string;
  color: string;
  createdAt: string;
  description: string;
  prerequisites: unknown[];
  gen_ed_requirements: string[];
}

/**
 * Course input for cleaning - accepts various course formats
 */
export interface CourseForUI {
  code: string;
  title?: string;
  course_title?: string;
  credits?: number;
  description?: string;
  gen_ed_requirements?: string[];
  isPlaceholder?: boolean;
  placeholderType?: string;
  requirements?: PlaceholderRequirements;
  notes?: string;
  color?: string;
  createdAt?: string;
  updatedAt?: string;
  status?: string; // Allow any string, caller will cast to EnrollmentStatus
  prerequisites?: unknown; // Legacy field preserved by adapter back-compat path
  prerequisites_raw?: string | null;
  corequisites_raw?: string | null;
  restriction?: string | null;
}

/**
 * Cleaned course for UI display
 */
export interface CleanedCourse {
  code: string;
  title: string | undefined;
  course_title: string | undefined;
  credits: number;
  description: string;
  gen_ed_requirements: string[];
  gen_ed_resolved: string[] | undefined;
  prerequisites_raw: string | null;
  corequisites_raw: string | null;
  restriction: string | null;
  isPlaceholder: boolean;
  placeholderType: string | undefined;
  requirements: PlaceholderRequirements | undefined;
  notes: string | undefined;
  color: string | undefined;
  createdAt: string | undefined;
  updatedAt: string | undefined;
}

/**
 * Decision log entry for student profile
 */
export interface DecisionLogEntry {
  type: 'added' | 'removed' | 'moved';
  course: string;
  semester?: string;
  from_semester?: string;
  to_semester?: string;
  [key: string]: unknown;
}

// =============================================================================
// FUNCTIONS
// =============================================================================

/**
 * Generate empty semester structure in CHRONOLOGICAL ORDER (NUMERIC FORMAT)
 * Only include default 8 semesters (4 years of fall/spring)
 */
export const generateEmptySemesters = (): SemestersMap => {
  return {
    '202408': [],  // Fall 2024
    '202501': [],  // Spring 2025
    '202508': [],  // Fall 2025
    '202601': [],  // Spring 2026
    '202608': [],  // Fall 2026
    '202701': [],  // Spring 2027
    '202708': [],  // Fall 2027
    '202801': []   // Spring 2028
  };
};

/**
 * Transform backend courses_by_semester format to frontend semesters format
 * Backend: { "202408": { courses: [course1, course2], credits: 16 } }
 * Frontend: { "202408": [course1, course2] }
 *
 * @deprecated Use toDomainSemesters from adapters/plan directly for new code.
 *             This wrapper is kept for backward compatibility.
 */
export const transformCoursesData = (backendData: Record<string, unknown>): SemestersMap => {
  // Delegate to the adapter for consistent transformation
  const semesters = toDomainSemesters(backendData);

  // Preserve backwards compatibility: ensure prerequisites field exists
  // (adapter strips it, but some legacy code may expect it)
  Object.keys(semesters).forEach(semesterCode => {
    semesters[semesterCode] = semesters[semesterCode].map(course => ({
      ...course,
      prerequisites: (course as CourseForUI).prerequisites || [],
    })) as EnrolledCourse[];
  });

  return semesters;
};

/**
 * Helper function to generate a unique placeholder ID
 */
export const generatePlaceholderId = (): string => {
  return `placeholder-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
};

/**
 * Helper function to create a placeholder course object
 */
export const createPlaceholderCourse = ({
  title = 'Untitled Placeholder',
  credits = 3,
  placeholderType = 'elective',
  requirements = {},
  notes = '',
  color = 'slate'
}: CreatePlaceholderOptions = {}): PlaceholderCourse => {
  return {
    code: generatePlaceholderId(),
    isPlaceholder: true,
    title,
    credits,
    status: 'placeholder',
    placeholderType,
    requirements: {
      department: requirements.department || '',
      levelMin: requirements.levelMin || null,
      levelMax: requirements.levelMax || null,
      genEdCategories: requirements.genEdCategories || [],
      keywords: requirements.keywords || [],
      ...requirements
    },
    notes,
    color,
    createdAt: new Date().toISOString(),
    // Include standard course fields for compatibility
    course_title: title,
    description: notes || `Placeholder for: ${title}`,
    prerequisites: [],
    gen_ed_requirements: requirements.genEdCategories || []
  };
};

/**
 * Extract the persistable placeholder shape (#205) from a client course
 * object — exactly the keys the backend validates and stores in
 * course_enrollments.placeholder_data. Compatibility fields the client
 * synthesizes (course_title, description, gen_ed_requirements, status)
 * are deliberately excluded; the payload path re-synthesizes them.
 */
export const extractPlaceholderData = (course: {
  code: string;
  title?: string;
  credits?: number;
  placeholderType?: string;
  requirements?: Record<string, unknown>;
  notes?: string;
  color?: string;
  createdAt?: string;
  updatedAt?: string;
}): Record<string, unknown> => {
  const data: Record<string, unknown> = { code: course.code };
  if (course.title !== undefined) data.title = course.title;
  if (course.credits !== undefined) data.credits = course.credits;
  if (course.placeholderType !== undefined) data.placeholderType = course.placeholderType;
  if (course.requirements !== undefined) data.requirements = course.requirements;
  if (course.notes !== undefined) data.notes = course.notes;
  if (course.color !== undefined) data.color = course.color;
  if (course.createdAt !== undefined) data.createdAt = course.createdAt;
  if (course.updatedAt !== undefined) data.updatedAt = course.updatedAt;
  return data;
};

/**
 * Helper function to clean course data for consistent UI display
 * Removes complex nested objects that cause display issues
 */
export const cleanCourseForUI = <T extends CourseForUI>(course: T | null | undefined): CleanedCourse | null => {
  if (!course) return null;

  return {
    code: course.code,
    title: course.title || course.course_title,
    course_title: course.title || course.course_title,
    credits: course.credits || 0,
    description: course.description || '',
    gen_ed_requirements: Array.isArray(course.gen_ed_requirements) ? course.gen_ed_requirements : [],
    // Without this the OR-area dropdown falls back to the first option during
    // the optimistic-add window (until refreshPlan returns the real payload).
    gen_ed_resolved: Array.isArray((course as { gen_ed_resolved?: string[] }).gen_ed_resolved)
      ? (course as { gen_ed_resolved?: string[] }).gen_ed_resolved
      : undefined,
    // Remove complex prerequisites object - CourseCard doesn't need them.
    // Keep the catalog text so an optimistic add shows it before refresh.
    prerequisites_raw: course.prerequisites_raw ?? null,
    corequisites_raw: course.corequisites_raw ?? null,
    restriction: course.restriction ?? null,
    // Any other course properties that don't cause display issues
    isPlaceholder: course.isPlaceholder || false,
    placeholderType: course.placeholderType,
    requirements: course.requirements,
    notes: course.notes,
    color: course.color,
    createdAt: course.createdAt,
    updatedAt: course.updatedAt
  };
};

/**
 * V2: Log a course decision to the student profile
 * Debounced batch: accumulates decisions and flushes after 500ms of inactivity,
 * sending them in parallel. Removes network calls from the critical UI path
 * during bulk operations (templates, multi-add, etc.).
 */
const DECISION_FLUSH_DELAY_MS = 500;
let decisionBuffer: DecisionLogEntry[] = [];
let decisionFlushTimer: ReturnType<typeof setTimeout> | null = null;

const flushDecisions = async (): Promise<void> => {
  const batch = decisionBuffer;
  decisionBuffer = [];
  decisionFlushTimer = null;

  if (batch.length === 0) return;

  try {
    const { typedApi } = await import('../../services/typedApiClient');
    await Promise.all(batch.map(d => typedApi.studentProfile.logDecision(d)));
  } catch (error) {
    const err = error as Error;
    console.warn('Failed to log decisions to profile:', err.message);
  }
};

export const logDecisionToProfile = (
  userId: string | undefined,
  decision: DecisionLogEntry
): void => {
  if (!userId) return;

  decisionBuffer.push(decision);

  if (decisionFlushTimer) {
    clearTimeout(decisionFlushTimer);
  }
  decisionFlushTimer = setTimeout(flushDecisions, DECISION_FLUSH_DELAY_MS);
};
